import { useState, useEffect, useRef } from 'react'
import {
  Download,
  Monitor,
  Apple,
  Loader2,
  Zap,
  Shield,
  Clock,
  ChevronDown,
  MousePointerClick,
  Settings,
  CheckCircle2
} from 'lucide-react'
import api from '../utils/api'
import SEO from '../components/SEO'

const BRAND = import.meta.env.VITE_APP_NAME || 'ZipWin'

// 主题色（仿 360 压缩落地页绿色）
const THEME_GREEN = '#1AB956'
const THEME_GREEN_DARK = '#17A54D'
const THEME_GREEN_LIGHT = '#E8F9EF'
const THEME_DARK_BG = '#1A1B23'
const THEME_TITLE = '#1D2129'

// 安装步骤（仿 360 压缩落地页）
const INSTALL_STEPS = [
  { icon: Download, title: '软件下载', desc: `从官网下载 ${BRAND} 安装包` },
  { icon: MousePointerClick, title: '一键安装', desc: '双击安装包，自动完成安装' },
  { icon: Settings, title: '极速安装中', desc: '稍等片刻，无需任何配置' },
  { icon: CheckCircle2, title: '完成', desc: `打开 ${BRAND}，开始压缩解压` }
]

// 官网真实截图（图源：backend/uploads/images/home/，相对路径跟随当前站点域名）
const HERO_SHOT_IMAGE = '/uploads/images/home/index.png'
const FEATURE_SHOTS = [
  { label: '解压文件列表界面', image: '/uploads/images/home/run-1.png' },
  { label: '一键压缩设置', image: '/uploads/images/home/run-2.png' },
  { label: '全格式支持演示', image: '/uploads/images/home/run-3.png' }
]

// 支持的压缩格式
const FORMATS = ['ZIP', '7Z', 'RAR', 'TAR', 'GZ', 'BZ2', 'XZ', 'ISO']

// 我们的优势
const ADVANTAGES = [
  { icon: Zap, title: '高效办公', desc: '智能选择最优压缩方式，压缩解压一键完成，办公更省时' },
  { icon: Shield, title: '安全加密', desc: '支持密码保护，本地处理不上传，文件安全不泄密' },
  { icon: Clock, title: '秒速解压', desc: '大文件快速解压，等待更少、效率更高' }
]

// 常见问题
const FAQS = [
  { q: `${BRAND} 是否免费？`, a: `${BRAND} 提供免费下载与安装，基础压缩、解压功能开箱即用。` },
  { q: '支持哪些操作系统？', a: '目前支持 Windows 10/11，更多平台版本正在准备中，敬请期待。' },
  { q: '支持哪些压缩格式？', a: '支持 ZIP、7Z、RAR、TAR、GZ、BZ2、XZ、ISO 等常见压缩格式，压缩解压一键搞定。' },
  { q: '如何解压带密码的压缩包？', a: '解压时输入正确密码即可正常解压，忘记密码的文件暂无法恢复。' }
]

// 下载按钮 + 三平台下拉（Hero 与底部下载区共用）
function DownloadCTA({ menuItems, loading, downloading, isWindowsOS, onDownload, onUnavailable }) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)

  // 点击下拉列表外部自动收起
  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [open])

  // 系统识别：Windows 点击直接下载并展开下拉；Mac/其他系统仅展开下拉自选
  const handleMainClick = () => {
    if (loading) return
    if (isWindowsOS) {
      const windowsItem = menuItems.find(item => item.id.includes('windows'))
      if (windowsItem) onDownload(windowsItem.id)
      setOpen(true)
    } else {
      setOpen(prev => !prev)
    }
  }

  return (
    <div
      className="relative inline-block pb-2 -mb-2"
      ref={wrapperRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        onClick={handleMainClick}
        disabled={loading || downloading}
        className="inline-flex items-center gap-2.5 bg-[#1AB956] hover:bg-[#17A54D] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold rounded-xl px-7 py-3.5 text-base transition-colors shadow-lg shadow-[#1AB956]/25"
      >
        {loading || downloading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Download className="w-5 h-5 flex-shrink-0" />
        )}
        <span>立即下载</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul className="absolute left-1/2 -translate-x-1/2 top-full w-64 bg-white rounded-xl border border-gray-200 shadow-xl py-1.5 z-[100]">
          {menuItems.map((item) => {
            const isCurrentDevice = isWindowsOS && item.id.includes('windows')
            return (
              <li key={item.id}>
                <button
                  onClick={() => {
                    setOpen(false)
                    if (item.item) {
                      onDownload(item.item.id)
                    } else {
                      onUnavailable()
                    }
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-gray-500 flex-shrink-0">
                    {item.icon === 'windows' ? <Monitor className="w-5 h-5" /> : <Apple className="w-5 h-5" />}
                  </span>
                  <span className="flex-1 flex flex-col items-start min-w-0">
                    <span className="text-sm text-gray-800 font-medium whitespace-nowrap">{item.label}</span>
                    <span className="text-[11px] text-gray-400 whitespace-nowrap">{item.meta}</span>
                  </span>
                  {isCurrentDevice && (
                    <span className="flex-shrink-0 text-[10px] font-semibold text-[#1AB956] bg-[#E8F9EF] border border-[#BFE9D0] rounded-full px-2 py-0.5 whitespace-nowrap">当前设备</span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function Home({ downloadIds }) {
  const [downloads, setDownloads] = useState([])
  const [downloading, setDownloading] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showWaitModal, setShowWaitModal] = useState(false)
  const [openFaq, setOpenFaq] = useState(0)

  useEffect(() => {
    fetchDownloads()
  }, [])

  // 官网访问埋点（不做回传）
  useEffect(() => {
    api.post('/website/visit', { page_url: window.location.href }).catch(() => {})
  }, [])

  const fetchDownloads = async () => {
    try {
      const response = await api.get('/downloads')
      if (response.data.success) {
        setDownloads(response.data.downloads)
      }
    } catch (error) {
      console.error('获取下载列表失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (id) => {
    // 官网下载埋点（点击即算，不做回传）
    api.post('/website/download-event', { os: id, page_url: window.location.href }).catch(() => {})

    // 首先判断 downloads 表里面有没有这个链接
    const targetDownload = downloads.find(d => d.id === id)
    if (!targetDownload || !targetDownload.file_path) {
      setShowWaitModal(true)
      return
    }

    setDownloading(id)

    try {
      const response = await api.get(`/downloads/${id}?t=${Date.now()}`, {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      })

      const contentType = response.headers?.['content-type'] || response.data?.type || ''
      if (contentType.includes('application/json')) {
        const reader = new FileReader()
        reader.onload = () => {
          const parsed = JSON.parse(reader.result)
          const data = parsed.data || parsed // 兼容被 server.js 包装过的格式
          if (data.redirectUrl) {
            let finalUrl = data.redirectUrl
            if (finalUrl.startsWith('/')) {
              finalUrl = `${window.location.origin}${finalUrl}`
            }
            window.location.href = finalUrl
          } else {
            alert(data.message || parsed.message || '安装包即将上线，敬请期待！')
          }
        }
        reader.readAsText(response.data)
      } else {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', targetDownload?.filename || (id.startsWith('macos-') ? `${BRAND}-${id}.dmg` : `${BRAND}-${id}.exe`))
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      if (error.response?.data?.message) {
        alert(error.response.data.message)
      } else {
        alert('下载失败，请稍后重试')
      }
    } finally {
      setDownloading(null)
    }
  }

  // 下载按钮模板：官方 id 共用同一套文案与图标
  // fallbackMeta 为接口无 platform/arch 数据时的兜底
  const heroButtonTemplates = {
    windows: { fallbackLabel: '下载 Windows 版本', fallbackMeta: 'WINDOWS 10/11 X64', icon: 'windows' },
    'macos-intel': { fallbackLabel: '下载 Mac Intel 版本', fallbackMeta: 'MACOS 12+ INTEL', icon: 'apple' },
    'macos-apple': { fallbackLabel: '下载 Mac Apple Silicon 版本', fallbackMeta: 'M2+ APPLE SILICON', icon: 'apple' }
  }

  // 下载渠道默认列表：macOS 安装包暂未上架，官网先只显示 Windows（其他渠道隐藏）
  // 模板保留 macOS 两项，安装包就绪后把 id 加回默认列表即可恢复
  const heroDownloadButtons = (downloadIds || ['windows']).map((id) => {
    const tpl = heroButtonTemplates[id] || (id.includes('windows') ? heroButtonTemplates.windows : id.includes('intel') ? heroButtonTemplates['macos-intel'] : heroButtonTemplates['macos-apple'])
    return { id, ...tpl }
  })

  // 下拉菜单项：接口 platform/arch 优先（后台维护的实际支持条件），无数据时用模板兜底
  const downloadMenuItems = heroDownloadButtons.map((buttonItem) => {
    const matchedDownloads = downloads.filter(d => d.id === buttonItem.id)
    const item = matchedDownloads.length > 0 ? matchedDownloads[0] : null
    const menuLabel = buttonItem.id.includes('windows') ? 'Windows 版' : buttonItem.id.includes('intel') ? 'Mac Intel 版' : 'Mac Apple Silicon 版'
    const metaText = [item?.platform, item?.arch].filter(Boolean).join(' · ') || buttonItem.fallbackMeta
    return { id: buttonItem.id, label: menuLabel, meta: metaText, icon: buttonItem.icon, item }
  })

  const isWindowsOS = typeof navigator !== 'undefined' && /windows/i.test(navigator.userAgent)

  const ctaProps = {
    menuItems: downloadMenuItems,
    loading,
    downloading,
    isWindowsOS,
    onDownload: handleDownload,
    onUnavailable: () => setShowWaitModal(true)
  }

  return (
    <div className="animate-in">
      <SEO
        title={`${BRAND}官网 - 免费高速压缩解压工具下载`}
        description={`${BRAND} 是一款免费高效的压缩解压工具，支持 ZIP、7Z、RAR 等多种格式，秒速压缩解压，一键搞定。下载安装即可使用。`}
        keywords={`${BRAND},压缩软件,解压软件,免费压缩工具,文件压缩`}
      />

      {/* 1. Hero 深色横幅（仿 360 压缩：左图右文） */}
      <section className="relative bg-[#1A1B23] text-white overflow-hidden">
        {/* 背景光效 */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#1AB956]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#1AB956]/5 blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* 文案（移动端在前，桌面在右） */}
            <div className="lg:order-2">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.25] tracking-tight">
                {BRAND}压缩，免费，高效，
                <br className="hidden sm:block" />
                秒速解压
              </h1>
              <p className="mt-5 sm:mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-xl">
                2026版最新，{BRAND}官方推出的一款可以快速压缩、解压文件的便携工具！
              </p>
              {/* 更新时间暂写死为今天，后续可改为从后台下载数据 updated_at 动态读取 */}
              <p className="mt-4 text-xs sm:text-sm text-white/50">
                更新时间：2026-09-24　软件版本：v1.0.0　应用平台：Windows
              </p>
              <div className="mt-8">
                <DownloadCTA {...ctaProps} />
              </div>
            </div>

            {/* 产品主图（桌面在左）：backend/uploads/images/home/index.png */}
            <div className="lg:order-1">
              <img
                src={HERO_SHOT_IMAGE}
                alt={`${BRAND} 产品界面`}
                className="w-full rounded-2xl border border-white/10 object-cover aspect-[16/10]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 2. 安装步骤 */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-[#1D2129]">安装步骤</h2>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {INSTALL_STEPS.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-[#E8F9EF] flex items-center justify-center">
                  <step.icon className="w-7 h-7 text-[#1AB956]" strokeWidth={2} />
                </div>
                <h3 className="mt-4 text-base font-semibold text-[#1D2129]">{step.title}</h3>
                <p className="mt-2 text-sm text-[#999999] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. 功能展示（仿 360"专业压缩，高效快速"） */}
      <section className="py-14 sm:py-20 bg-[#F7F8FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-[#1D2129]">专业压缩，高效快速</h2>
          <p className="mt-3 text-center text-sm sm:text-base text-[#999999] max-w-2xl mx-auto">
            秒速解压无需等待，高效压缩随手完成，支持格式全，一键搞定更省心！
          </p>
          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {FEATURE_SHOTS.map((shot) => (
              <div key={shot.image} className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
                <img
                  src={shot.image}
                  alt={`${BRAND} ${shot.label}`}
                  className="w-full aspect-[3/2] object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. 支持格式 */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-[#1D2129]">支持格式全面</h2>
          <p className="mt-3 text-center text-sm sm:text-base text-[#999999]">常见压缩格式全覆盖，压缩解压一键搞定</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {FORMATS.map((format) => (
              <span key={format} className="px-5 py-2 rounded-full bg-[#F7F8FA] border border-gray-200 text-sm font-semibold text-[#1D2129]">
                {format}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 5. 我们的优势（仿 360"我们的优势"） */}
      <section className="py-14 sm:py-20 bg-[#F7F8FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-[#1D2129]">我们的优势</h2>
          <p className="mt-3 text-center text-sm sm:text-base text-[#999999]">压缩解压一键处理，助力办公，安全省时</p>
          <div className="mt-10 grid sm:grid-cols-3 gap-8 lg:gap-12">
            {ADVANTAGES.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-[#E8F9EF] flex items-center justify-center">
                  <item.icon className="w-7 h-7 text-[#1AB956]" strokeWidth={2} />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-[#1D2129]">{item.title}</h3>
                <p className="mt-2 text-sm text-[#666666] leading-relaxed max-w-xs mx-auto">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. 常见问题 */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-[#1D2129]">常见问题</h2>
          <div className="mt-10 space-y-3">
            {FAQS.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm sm:text-base font-medium text-[#1D2129]">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-4 text-sm text-[#666666] leading-relaxed">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. 底部下载区 */}
      <section className="py-14 sm:py-20 bg-[#1A1B23] text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold">立即下载 {BRAND}</h2>
          <p className="mt-3 text-sm sm:text-base text-white/70">免费下载安装，即刻体验高速压缩解压</p>
          <div className="mt-8">
            <DownloadCTA {...ctaProps} />
          </div>
        </div>
      </section>

      {/* 敬请期待弹窗 */}
      {showWaitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-0">
          <div
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setShowWaitModal(false)}
          ></div>

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 sm:p-8 border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-[#E8F9EF] rounded-full flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-[#1AB956]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                当前安装包敬请期待
              </h3>
              <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                该版本的安装包正在加紧制作中，很快就会与您见面，感谢您的关注与支持。
              </p>
              <button
                onClick={() => setShowWaitModal(false)}
                className="w-full py-3 px-4 bg-[#1AB956] hover:bg-[#17A54D] text-white font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-[#1AB956] focus:ring-offset-2"
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Home
